<template>
  <div
    v-if="show_field"
    class="form-group"
    :class="
      Object.assign(
        {
          'h-80': rows === 0,
          'mb-0': rows === 0 || no_mb,
          'no-input': noInput,
        },
        fgClasses
      )
    "
  >
    <label
      v-if="label"
      :class="{ 'mb-0': !disabled && error }"
      :style="{ 'font-weight': font_bold ? 'bold' : 'normal' }"
      >{{ label_ }}</label
    >
    <slot name="icon_action" />
    <b-badge
      v-if="help && help.length > 0"
      v-b-tooltip
      class="pull-right mt-1 mb-1"
      :title="help"
      variant="default"
    >
      <i
        class="far fa-info-circle ml-1 fa-lg"
        style="color: #61a0ff"
      />
    </b-badge>
    <b-badge
      v-if="markdown && !disabled && !enable_markdow_editor"
      v-b-tooltip
      class="pull-right bg-gray"
      :title="_('You can use markdown in this field')"
      variant="default"
    >
      <i class="far fa-font" />
    </b-badge>
    <div
      v-if="(!disabled && error) || (show_error_disabled && error)"
      class="error-container"
    >
      <span
        v-if="!custom_error_message"
        class="invalid-feedback"
        data-testid="form-field-error"
      >
        {{ error }}
      </span>
      <span
        v-else
        class="invalid-feedback"
      >
        {{ custom_error_message }}
      </span>
    </div>
    <template v-if="!noInput">
      <div
        v-if="rows === undefined && !disabled && !show_input"
        class="input-group"
      >
        <input
          ref="input_field"
          v-model="value_"
          :aria-placeholder="placeholder"
          :autocomplete="autocomplete"
          class="form-control"
          :class="{
            'is-invalid': !!error && touched && !noCheck && !noValidateCls,
            'is-valid': !error && !disabled && touched && !noCheck && !noValidateCls,
          }"
          :data-hidden="datahidden"
          data-testid="form-field-input"
          :min="min"
          :name="name"
          :placeholder="!disabled ? placeholder : ''"
          :type="type"
          v-on="!stopEvent ? { change: changed } : {}"
          @blur="blured"
          @keydown="type === 'number' ? onlyNumber($event, float) : null"
          @keyup="changed"
          @keyup.enter.prevent="$emit('enter')"
          @keyup.esc="$emit('esc')"
        />
        <div
          v-if="unit"
          class="input-group-append"
        >
          <span class="input-group-text">{{ unit }}</span>
        </div>
        <div
          v-if="has_reset && value_ && value_.length > 0"
          class="input-group-append"
          @click="$emit('reset')"
        >
          <span class="input-group-text custom-background"
            ><i
              class="far fa-times-circle"
              style="color: #ff0000"
          /></span>
        </div>
        <div
          v-if="show_password"
          class="input-group-append"
          @click="$emit('toggle_visibility')"
        >
          <span class="input-group-text">
            <i
              v-if="type === 'password'"
              class="fa fa-eye"
            />
            <i
              v-else
              class="fa fa-eye-slash"
            />
          </span>
        </div>
        <div
          v-if="type === 'url'"
          class="input-group-append"
          @click="openNewTab()"
        >
          <span
            class="input-group-text"
            :title="_('Go to')"
          >
            <i class="fa fa-external-link" />
          </span>
        </div>
      </div>
      <div
        v-else-if="show_input"
        :style="{ 'min-height': '40px' }"
      >
        <input
          ref="input_field"
          v-model="value_"
          :aria-placeholder="placeholder"
          :autocomplete="autocomplete"
          class="form-control"
          :class="{
            'is-invalid': !!error && touched && !noCheck && !noValidateCls,
            'is-valid': !error && !disabled && touched && !noCheck && !noValidateCls,
          }"
          :data-hidden="datahidden"
          :disabled="disabled"
          :min="min"
          :name="name"
          :placeholder="!disabled ? placeholder : ''"
          :type="type"
          v-on="!stopEvent ? { change: changed } : {}"
          @blur="blured"
          @keydown="type === 'number' ? onlyNumber($event, float) : null"
          @keyup="changed"
          @keyup.enter.prevent="$emit('enter')"
          @keyup.esc="$emit('esc')"
        />
      </div>
      <div
        v-else-if="rows === undefined && !show_input"
        :style="{ 'min-height': '40px' }"
      >
        <span v-if="not_set && not_set_label"
          ><i> {{ not_set_label }}</i></span
        >
        <span
          v-else-if="not_set"
          class="text-muted"
        >
          <i>{{ _('Not set') }}</i>
        </span>
        <!-- Following href is trusted -->
        <a
          v-else-if="type === 'url' && isURLFormatValid(value_)"
          :href="value_"
          target="_blank"
        >
          {{ truncate(value_, 50) }}
        </a>
        <span v-else>
          {{ value_ }}
        </span>
        <span v-if="unit && !not_set">
          <span>{{ unit }}</span>
        </span>
      </div>

      <NdsMarkdownEditor
        v-if="rows !== undefined && !disabled && markdown && enable_markdow_editor"
        ref="editor"
        v-model="value_"
        :height="editorHeight"
        mode="wysiwyg"
        :options="editorOptions"
        preview-style="tab"
        @blur="bluredMarkdown"
        @input="editorChanged"
      />

      <textarea
        v-if="rows !== undefined && !disabled && (!markdown || !enable_markdow_editor)"
        v-model="value_"
        :aria-placeholder="placeholder"
        class="form-control"
        :class="{
          'is-invalid': !!error && touched && !noValidateCls,
          'is-valid': !error && !disabled && touched && !noValidateCls,
          'h-100': rows === 0,
        }"
        :disabled="disabled"
        :name="name"
        :placeholder="!disabled ? placeholder : ''"
        :rows="rows"
        @blur="blured"
        @change="changed"
        @keyup="changed"
      />
      <div v-if="rows !== undefined && disabled && markdown && value">
        <NdsMarkdownViewer
          :sanitize="sanitize"
          :value="value"
        />
      </div>
      <div
        v-if="rows !== undefined && disabled && !markdown && value"
        :style="{ 'min-height': '40px' }"
      >
        {{ value_ }}
      </div>
      <div
        v-else-if="rows !== undefined && disabled && not_set"
        class="text-muted"
        :style="{ 'min-height': '40px' }"
      >
        <span v-if="not_set_label"
          ><i> {{ not_set_label }}</i></span
        >
        <span v-else
          ><i>{{ _('Not set') }}</i></span
        >
      </div>
    </template>
    <template v-else>
      <input
        :name="name"
        type="hidden"
        :value="value"
      />
    </template>
  </div>
</template>

<script>
import { onlyNumber } from '@/helpers/Keyboard.helpers'
import { isURLFormatValid } from '@/helpers/StringFormat.helpers'
import { truncate } from '@/helpers/Text.helpers'
import { rootBus } from '@/contexts/shared/event-bus/RootBus'
import { nextTick } from 'vue'
import NdsMarkdownViewer from '@/components/NewDesignSystem/NdsMarkdown/NdsMarkdownViewer.vue'
import NdsMarkdownEditor from '@/components/NewDesignSystem/NdsMarkdown/NdsMarkdownEditor.vue'

/**
 * This component is used to render an input field, supporting multiple options to edit a single line, multiple line,
 * number, markdown, etc.
 * It emits the `input` event so it is compatible with v-model
 */
export default {
  name: 'FormField',
  filters: {
    /**
     * Filter for displaying number with space/dot separator
     * @public
     */
    thousandSep(value) {
      if (value) {
        return `$${value.toLocaleString()}`
      } else {
        return ''
      }
    },
  },
  components: {
    NdsMarkdownViewer,
    NdsMarkdownEditor,
  },
  props: {
    font_bold: {
      type: Boolean,
      default: true,
    },
    show_input: {
      type: Boolean,
      default: false,
    },
    show_error_disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * This is the name of the field, it should be unique within the form
     */
    name: String,
    /**
     * This is the label of the field
     */
    label: String,
    /**
     * This is the label of the unseted disabled field (optional)
     */
    not_set_label: {
      type: String,
      default: null,
    },
    /**
     * Flag. If true, no margin bottom will be added to the field
     */
    no_mb: Boolean,
    /**
     * This classes will be added to the form-group element
     */
    fgClasses: {
      type: Object,
      default: () => {},
    },
    /**
     * This will be send to the input element
     */
    autocomplete: {
      type: [Boolean, String],
      default: 'false',
    },
    autofocus: {
      type: [Boolean, String],
      default: 'false',
    },
    /**
     * If true, a reset event will be emitted on click on the input-group-append element
     */
    has_reset: {
      type: Boolean,
      default: false,
    },
    stopEvent: {
      type: Boolean,
      default: false,
    },
    /**
     * Forwarded to the input element
     */
    datahidden: {
      type: Boolean,
      default: false,
    },
    /**
     * Forwarded to the input element
     */
    hide_dual_mode: {
      type: Boolean,
      default: true,
    },
    sanitize: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: [String, Number],
    },
    /**
     * If defined, this will show a question mark on the right, with given text as tooltip
     */
    help: String,
    /**
     * flag to indicate that no validation should be done on the field
     */
    'no-validate-cls': {
      type: Boolean,
      default: false,
    },
    /**
     * flag to indicate that no validation should be done on the field
     */
    'no-check': {
      type: Boolean,
      default: false,
    },
    /**
     * flag to indicate that the content is markdown
     */
    markdown: {
      type: Boolean,
      default: false,
    },
    /**
     * Overriddes the v-validate error message
     */
    custom_error_message: {
      type: String,
    },
    /**
     * If true, the field is hidden
     */
    'no-input': {
      type: Boolean,
      default: false,
    },
    /**
     * If true, the field is disabled (non editable)
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    show_password: {
      type: Boolean,
      default: false,
    },
    /**
     * The error string (coming from v-validate in most cases)
     */
    error: {
      type: String,
      required: false,
    },
    /**
     * A input-group-append will be added with the content of this props
     */
    unit: {
      type: String,
      required: false,
    },
    /**
     * Forwarded to the input element
     */
    min: {
      type: Number,
      default: 0,
      required: false,
    },
    /**
     * The type of input
     * @values url, text, password, email, search, number
     */
    type: {
      type: String,
      default: 'text',
      validator: (val) => {
        return ['url', 'text', 'password', 'email', 'search', 'number'].indexOf(val) !== -1
      },
    },
    float: {
      type: Boolean,
      default: false,
      required: false,
    },
    rows: [Number, String],
    value: {
      type: [String, Number],
    },
    toolbar_items: {
      type: Array,
      default: () => [
        'heading',
        'bold',
        'italic',
        'divider',
        'hr',
        'quote',
        'divider',
        'ul',
        // 'ol',
        // 'task',
        // 'indent',
        // 'outdent',
        // 'divider',
        // 'table',
        'image',
        'link',
        // 'divider',
        // 'code',
        // 'codeblock'
      ],
    },
  },
  data: function () {
    return {
      touched: false,
      value_: null,
      enable_markdow_editor: true,
      editorOptions: {},
    }
  },
  computed: {
    show_field: function () {
      return true
    },
    editorHeight: function () {
      const EDITOR_HEADER_HEIGHT = 45
      const EDITOR_ROW_HEIGHT = 25
      const rows = +this.rows || 3

      return rows * EDITOR_ROW_HEIGHT + EDITOR_HEADER_HEIGHT
    },
    label_: function () {
      if (this.disabled) {
        return this.label.replace('*', '')
      }
      return this.label
    },
    not_set: function () {
      return this.disabled && (this.value === '' || this.value == null || typeof this.value == 'undefined')
    },
  },
  watch: {
    value: function (val) {
      this.value_ = val
    },
  },
  beforeDestroy() {
    rootBus.off('data:submit', this.onDataSubmit)
  },
  mounted() {
    this.value_ = this.value
    rootBus.on('data:submit', this.onDataSubmit)
  },
  methods: {
    onDataSubmit: function () {
      this.touched = false
    },
    isURLFormatValid,
    onlyNumber,
    truncate,
    bluredMarkdown() {
      this.blured()
    },
    openNewTab() {
      if (this.value_) {
        window.open(this.value_, '_blank')
      }
    },
    focus() {
      if (this.$refs.input_field) {
        nextTick(() => {
          this.$refs.input_field.focus()
        })
      } else {
        this.$log.debug('INPUT FIELD NOT PRESENT')
      }
    },
    changed: function (e) {
      this.touched = true
      /**
       * Classical input event, which allows v-modal to be used
       */
      this.$emit('input', e.target.value)
    },
    triggerCheckValue() {
      this.$emit('check-value', this.value_)
    },
    editorChanged: function () {
      this.$emit('input', this.value_)
    },
    blured: function () {
      /**
       * Forward blur event
       */
      this.$emit('blur')
    },
    reset: function () {
      this.touched = false
    },
  },
}
</script>

<style scoped>
.fieldOuter {
  width: 100%;
}

.fieldOuter label {
  position: absolute;
  left: 12px;
  top: 10px;
  line-height: 15px;
  transition: all 0.1s;
  overflow: hidden;
  font-weight: 300;
  font-size: 14px;
  color: #222;
  white-space: nowrap;
  z-index: 20;
  opacity: 0;
  outline: none;
  pointer-events: none;
}

.fieldOuter input:focus + label {
  opacity: 1;
  top: 1px;
}

.fieldOuter input:focus {
  outline: none;
  border-color: rgba(82, 168, 236, 0.8);
  padding-top: 15px;
}
</style>
